/**
 * <snake-search>: header search box with live suggestions.
 * Suggestions come from Shopify predictive search, rendered by sections/snake-predictive-search.liquid.
 * Keyboard: ↑/↓ move through suggestions, Enter opens one (or searches), Esc closes.
 */
const SUGGEST_URL = window.Theme?.routes?.predictive_search_url || '/search/suggest';
const SEARCH_URL = window.Theme?.routes?.search_url || '/search';
const RECENT_KEY = 'snk-recent-searches';

/** Recent searches live only in this browser (localStorage), never sent anywhere. */
const recent = {
  get() {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter((q) => typeof q === 'string').slice(0, 6) : [];
    } catch (e) {
      return [];
    }
  },
  add(q) {
    const query = String(q || '').trim();
    if (!query) return;
    const list = [query, ...recent.get().filter((x) => x.toLowerCase() !== query.toLowerCase())].slice(0, 6);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {}
  },
  clear() {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch (e) {}
  },
};

class SnakeSearch extends HTMLElement {
  connectedCallback() {
    this.input = /** @type {HTMLInputElement} */ (this.querySelector('input[type="search"]'));
    this.panel = /** @type {HTMLElement} */ (this.querySelector('.snk-search__panel'));
    this.clearBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.snk-search__clear'));
    this.cancelBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.snk-search__cancel'));
    this.recentTpl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-snk-search-recent]'));
    if (!this.input || !this.panel) return;

    /** @type {AbortController | null} */
    this.controller = null;
    this.timer = 0;
    this.active = -1;
    this.cache = new Map();

    this.input.addEventListener('focus', this.onFocus);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.clearBtn?.addEventListener('click', this.onClear);
    this.cancelBtn?.addEventListener('click', () => this.close(true));
    document.addEventListener('pointerdown', this.onOutside);
    this.querySelector('form')?.addEventListener('submit', () => recent.add(this.query));
    this.panel.addEventListener('click', (e) => {
      const el = /** @type {HTMLElement} */ (e.target);
      if (el.closest('[data-clear-recent]')) {
        e.preventDefault();
        recent.clear();
        this.close();
        this.input.focus();
        return;
      }
      if (el.closest('a') && this.query) recent.add(this.query);
    });
    this.syncClear();

    // the full placeholder doesn't fit on phones
    const mq = window.matchMedia('(max-width: 989px)');
    const full = this.input.placeholder;
    this.basePlaceholder = () => (mq.matches ? 'Search snacks' : full);
    const setPlaceholder = () => (this.input.placeholder = this.basePlaceholder());
    mq.addEventListener('change', setPlaceholder);
    setPlaceholder();
    this.startExamples();
  }

  /** While the box is empty and idle, type out example searches: "Search Pocky…", "Search matcha…" */
  startExamples() {
    const examples = (this.dataset.examples || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!examples.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let i = 0;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const idle = () => !this.input.value && document.activeElement !== this.input && !document.hidden;
    const run = async () => {
      await sleep(2600);
      for (;;) {
        if (!idle()) {
          this.input.placeholder = this.basePlaceholder();
          await sleep(1500);
          continue;
        }
        const word = examples[i % examples.length];
        i++;
        for (let n = 0; n <= word.length; n++) {
          if (!idle()) break;
          this.input.placeholder = `Search ${word.slice(0, n)}`;
          await sleep(70);
        }
        await sleep(1500);
        for (let n = word.length; n >= 0; n--) {
          if (!idle()) break;
          this.input.placeholder = `Search ${word.slice(0, n)}`;
          await sleep(35);
        }
        if (i % examples.length === 0) {
          this.input.placeholder = this.basePlaceholder();
          await sleep(3000);
        }
      }
    };
    run();
    this.input.addEventListener('focus', () => (this.input.placeholder = this.basePlaceholder()));
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutside);
  }

  get query() {
    return this.input.value.trim();
  }

  onFocus = () => {
    if (!this.query) {
      this.showEmpty();
      return;
    }
    this.openPanel();
    this.fetchSuggestions();
  };

  onInput = () => {
    this.syncClear();
    clearTimeout(this.timer);
    if (!this.query) {
      this.controller?.abort();
      this.showEmpty();
      return;
    }
    this.openPanel();
    this.panel.setAttribute('aria-busy', 'true');
    this.timer = window.setTimeout(() => this.fetchSuggestions(), 160);
  };

  onClear = () => {
    this.input.value = '';
    this.syncClear();
    this.showEmpty();
    this.input.focus();
  };

  /** @param {PointerEvent} e */
  onOutside = (e) => {
    if (!this.classList.contains('is-open')) return;
    if (this.contains(/** @type {Node} */ (e.target))) return;
    this.close();
  };

  /** @param {KeyboardEvent} e */
  onKeydown = (e) => {
    const options = this.options();
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!options.length) return;
      e.preventDefault();
      this.openPanel();
      const d = e.key === 'ArrowDown' ? 1 : -1;
      this.setActive((this.active + d + options.length) % options.length);
    } else if (e.key === 'Enter' && this.active >= 0 && options[this.active]) {
      e.preventDefault();
      options[this.active].click();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  };

  options() {
    return /** @type {HTMLElement[]} */ (Array.from(this.panel.querySelectorAll('[role="option"]')));
  }

  setActive(index) {
    const options = this.options();
    options.forEach((o, i) => {
      o.id = o.id || `snk-opt-${i}-${Math.random().toString(36).slice(2, 7)}`;
      o.classList.toggle('is-active', i === index);
      o.setAttribute('aria-selected', String(i === index));
    });
    this.active = index;
    const el = options[index];
    if (el) {
      this.input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  syncClear() {
    if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
  }

  openPanel() {
    const header = this.closest('[data-snk-header]');
    if (header) {
      document.documentElement.style.setProperty('--snk-hd-bottom', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    }
    this.classList.add('is-open');
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    if (this.cancelBtn) this.cancelBtn.hidden = false;
    document.documentElement.classList.add('snk-search-open');
  }

  /** @param {boolean} [blur] */
  close(blur = false) {
    this.classList.remove('is-open');
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    if (this.cancelBtn) this.cancelBtn.hidden = true;
    document.documentElement.classList.remove('snk-search-open');
    this.active = -1;
    if (blur) this.input.blur();
  }

  /** Empty box: show recent searches if there are any, otherwise keep the page visible. */
  showEmpty() {
    this.panel.removeAttribute('aria-busy');
    this.active = -1;
    const list = recent.get();
    if (!list.length || !this.recentTpl) {
      this.close();
      return;
    }
    const content = /** @type {DocumentFragment} */ (this.recentTpl.content.cloneNode(true));
    const ul = content.querySelector('[data-recent-list]');
    list.forEach((q) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'presentation');
      const a = document.createElement('a');
      a.className = 'snk-ps__query';
      a.setAttribute('role', 'option');
      a.href = `${SEARCH_URL}?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last`;
      a.innerHTML = '<svg class="snk-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>';
      const span = document.createElement('span');
      span.textContent = q;
      a.append(span);
      a.addEventListener('click', () => recent.add(q));
      li.append(a);
      ul?.append(li);
    });
    this.panel.replaceChildren(content);
    this.openPanel();
  }

  async fetchSuggestions() {
    const q = this.query;
    if (!q) return;
    this.controller?.abort();
    this.controller = new AbortController();

    if (this.cache.has(q)) {
      this.render(this.cache.get(q));
      return;
    }

    const params = new URLSearchParams({
      q,
      section_id: 'snake-predictive-search',
      'resources[type]': 'product,collection,query',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][prefix]': 'last',
    });
    try {
      const res = await fetch(`${SUGGEST_URL}?${params}`, { signal: this.controller.signal });
      if (!res.ok) throw new Error(String(res.status));
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.snk-ps');
      if (!content) throw new Error('no content');
      this.cache.set(q, content);
      if (this.query === q) this.render(content);
      this.dispatchEvent(
        new CustomEvent('snake:search-suggest', {
          bubbles: true,
          detail: { query: q, results: Number(content.getAttribute('data-count')) || 0 },
        })
      );
    } catch (e) {
      if (/** @type {Error} */ (e).name === 'AbortError') return;
      this.panel.removeAttribute('aria-busy');
      const msg = document.createElement('p');
      msg.className = 'snk-ps__error';
      msg.textContent = "Suggestions aren't loading right now. Press Enter to search.";
      this.panel.replaceChildren(msg);
    }
  }

  /** @param {Element} content */
  render(content) {
    this.panel.removeAttribute('aria-busy');
    this.panel.replaceChildren(content.cloneNode(true));
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
  }
}

if (!customElements.get('snake-search')) customElements.define('snake-search', SnakeSearch);
